import type {
  CardProps,
} from '@/scopes/prices/components/TransportSelector/Card'
import { useIntl } from '@/intl'
import {
  airlineCodesMap,
  space,
  SpaceBetween,
  tok,
  tokPx,
} from '@sunwebgroup/ui-components'
import { AirlineIcon, Button, Icon, Typography } from '@sunwebgroup/ui-components/uds'

import { identity } from 'ramda'
import * as React from 'react'

import { css, styled } from 'styled-components'

const Remark = styled(Typography)`
  color: ${tok('primitives_color_neutral_500')};
  ${Typography.modVariant('body-xs-regular-tight')};
`

const InfoItem = styled.div`
  display: inline-flex;
  flex-wrap: nowrap;
  align-items: center;
  margin-right: ${space('primitives_space_16')};
`

const AirlineWrapper = styled(SpaceBetween)`
  display: inline-flex;
  align-items: end;
  margin-right: ${space('primitives_space_16')};
`

const StyledAirlineIcon = styled(AirlineIcon)`
  height: 20px;
  width: 20px;
`

const InfoBlock = styled.div`
  margin-top: ${space('primitives_space_8')}!important;
  flex-wrap: wrap;

  ${Icon} {
    margin-right: ${space('primitives_space_4')};
  }
`
const ListItemContent = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
`

const CardContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: ${space('primitives_space_8')} ${space('primitives_space_8')} ${space('primitives_space_12')} ${space('primitives_space_8')};
`
const FeaturesWrapper = styled.div`
    display: flex;
    align-items: center;
    margin-top: ${space('primitives_space_8')};
    padding-top: ${space('primitives_space_12')};
    border-top: 2px solid ${tok('semantics_dividers_color')};
    width: 100%;
`

const ListItemRoot = styled.div<{ isSelected?: boolean }>`
  background-color: ${tok('semantics_views_background_color')};
  border: ${tokPx('semantics_dividers_tickness')} solid ${tok('semantics_dividers_color')};
  border-radius: ${tokPx('primitives_corner_radius_l')};
  cursor: pointer;
  &:first-child {
    margin-top: 0 !important;
  }
  &:nth-child(2) {
    margin-top: 0 !important;
  }
  ${({ isSelected }) => css`
    border: ${tokPx('semantics_dividers_tickness')} solid ${isSelected ? tok('primitives_color_selected_600') : tok('semantics_dividers_color')};
  `}
`
const PriceWrapper = styled.div<{ isSelected?: boolean }>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: ${space('primitives_space_12')};
    line-height: 1.75;
  ${({ isSelected }) => css`
    background: ${isSelected ? tok('primitives_color_selected_500') : tok('primitives_color_neutral_50')};
    color: ${isSelected ? tok('primitives_color_neutral_0') : tok('primitives_color_neutral_800')};
  `}
`
export function FlightCard({
  id,
  arrivalDate,
  departureDate,
  route,
  airline,
  luggage,
  flightType,
  price,
  isSelected,
  remark,
  setSelected = identity,
  noActiveState,
}: CardProps) {
  const intl = useIntl()

  const flightTypeLabel
    = flightType === 'standard'
      ? 'prices_transport_selector_flight_type_info_standard_value'
      : 'prices_transport_selector_flight_type_info_flexible_value'
  return (
    <ListItemRoot isSelected={isSelected && !noActiveState} onClick={() => !isSelected && !noActiveState ? setSelected(id) : null}>
      <CardContent>
        <ListItemContent>
          <Typography $variant="body-l-bold-tight">{`${route.from.name} ${route.from.code} - ${route.to.name} ${route.to.code}`}</Typography>
          <Typography $variant="body-m-regular-tight">{`${departureDate.time} - ${arrivalDate.time}`}</Typography>
          <FeaturesWrapper>
            <InfoBlock>
              {airline && (
                <AirlineWrapper direction="x" spacing={space('primitives_space_4')}>
                  <StyledAirlineIcon glyph={airlineCodesMap[airline.id]} size="md" />
                  <Typography $variant="body-s-regular-tight">{airline.name}</Typography>
                </AirlineWrapper>
              )}
              {luggage && flightType && luggage.map(luggageItem => (
                <InfoItem key={luggageItem.type}>
                  <Icon glyph={luggageItem.type === 'hold' ? 'LuggageCheck' : 'BackpackWithCheckMark'} />
                  <Typography $variant="body-s-regular-tight">
                    {luggageItem.type === 'hold' && `${luggageItem.weight} kg`}
                    {luggageItem.type === 'hand'
                    && (luggageItem.weight > 0
                      ? `${luggageItem.weight} kg`
                      : intl.formatMessage({ id: 'prices_transport_selector_no-hand-luggage' }))}
                  </Typography>
                </InfoItem>
              ))}
              <InfoItem>
                <Icon glyph={`${flightType === 'standard' ? 'AirplaneToTop' : 'AirplaneWithArrowsCirclePath'}`} />
                <Typography $variant="body-s-regular-tight">{intl.formatMessage({ id: flightTypeLabel })}</Typography>
              </InfoItem>
            </InfoBlock>
            {remark && <Remark>{remark}</Remark>}
          </FeaturesWrapper>
        </ListItemContent>
      </CardContent>
      <PriceWrapper isSelected={isSelected && !noActiveState}>
        <div>
          <Typography $variant="body-m-bold-tight">{intl.formatCurrency(price, true)}</Typography>
          <Typography $variant="body-m-bold-tight">
            {' '}
            {intl.formatMessage({ id: 'prices_transport_selector_price-per-person_abbreviation' })}
          </Typography>
        </div>
        { !isSelected && (
          <Button $size="S" $type="secondary" onClick={() => setSelected(id)}>
            {intl.formatMessage({
              id: 'prices_transport_selector_flight-selector-select',
            })}
          </Button>
        )}
      </PriceWrapper>
    </ListItemRoot>
  )
}
